import { ReactComponent as CustomCheckIcon } from '../../assets/icons/CheckmarkProformaStepper.svg'
import { ReactComponent as CustomCancelIcon } from '../../assets/icons/FailedProformaStepper.svg'
import SectionTitle from '../../components/SectionTitle'
import { gregorianToJalaaliWithHourMinuteAndSecond } from '../../helpers/date'
import { getTranslate, lang } from '../../localization'
import { ProformaStatus } from '../../types/walletEnums'
import { Proforma } from '../../types/walletProforma'
import useStyles from './styles/ProformaStatusStepper.styles'

interface ProformaStatusStepperProps {
  proforma: Proforma
  isOnlinePayment: boolean
}
const ProformaStatusStepper: React.FC<ProformaStatusStepperProps> = ({
  proforma,
  isOnlinePayment
}) => {
  const classes = useStyles()

  const terminalStatuses: ProformaStatus[] = [
    'rejected',
    'cancelled',
    'expired'
  ]

  const getStatusFlow = (isOfflinePayment: boolean): ProformaStatus[] => {
    return isOfflinePayment
      ? [
          'pendingConfirmation',
          'confirmed',
          'pendingPayment',
          'pendingPaymentConfirmation',
          'paid'
        ]
      : ['pendingConfirmation', 'confirmed', 'pendingPayment', 'paid']
  }

  const getSteps = (p: Proforma, isOfflinePayment: boolean) => {
    const statusFlow = getStatusFlow(isOfflinePayment)

    const pastStatuses = p.histories.slice(0, -1).map((h) => ({
      status: h.status,
      time: h.createdAt
    }))

    const currentStatus = p.status
    const currentStepIndex = statusFlow.indexOf(currentStatus)

    const currentStep = {
      status: currentStatus,
      time: p.updatedAt
    }

    const nextStatus = terminalStatuses.includes(currentStatus)
      ? null
      : statusFlow[currentStepIndex + 1] || null

    const nextStep = nextStatus
      ? {
          status: nextStatus,
          time: null
        }
      : null

    return {
      pastSteps: pastStatuses,
      currentStep,
      nextStep
    }
  }

  const { pastSteps, currentStep, nextStep } = getSteps(
    proforma,
    !isOnlinePayment
  )

  const steps = [...pastSteps, currentStep, ...(nextStep ? [nextStep] : [])]

  return (
    <div>
      <SectionTitle title="Transaction Status" marginBottom />
      <div>
        {steps.map((step, index) => {
          const isPast = index < pastSteps.length
          const isUpcoming = !isPast && step.time === null
          const isTerminal = terminalStatuses.includes(step.status)
          let statusColorClass = ''
          if (['confirmed', 'paid'].includes(step.status) && !isUpcoming) {
            statusColorClass = classes.secondaryColor
          } else if (step.status.startsWith('pending')) {
            statusColorClass = classes.infoColor
          } else if (isTerminal) {
            statusColorClass = classes.errorColor
          } else {
            statusColorClass = classes.disabledText
          }

          const lineColorClass =
            index < pastSteps.length - 1 || proforma.status == 'paid'
              ? classes.secondaryLine
              : classes.greyLine

          const timeClass = isUpcoming ? classes.disabledText : classes.timeText
          return (
            <div key={index} className={classes.stepContainer}>
              {index !== steps.length - 1 && (
                <div className={`${classes.verticalLine} ${lineColorClass}`} />
              )}

              <div className={classes.iconWrapper}>
                {isPast || proforma.status === 'paid' ? (
                  <CustomCheckIcon />
                ) : isTerminal ? (
                  <CustomCancelIcon />
                ) : (
                  <div className={`${classes.circleIcon}`} />
                )}
              </div>

              <div className={classes.stepContent}>
                <div className={`${classes.status} ${statusColorClass}`}>
                  {step.status}
                </div>
                <div className={timeClass}>
                  {step.time
                    ? lang === 'fa'
                      ? gregorianToJalaaliWithHourMinuteAndSecond(step.time)
                      : step.time
                    : getTranslate('Next Step')}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ProformaStatusStepper
